import { ServerExtendedServiceType, SERVICE_TYPE } from '@expane/data'
import {
  getThresholdTypeByGroupRateType,
  RATE_FROM_GROUP_SERVICES_TYPES,
  RATE_FROM_SERVICES_FALLBACK_TYPES,
  RATE_FROM_SERVICES_TYPES,
} from '@expane/logic/salarySettings'
import { findById } from '@expane/logic/utils'
import { Checkbox, Hint, Input, NumberInput, SelectDropdown, TableCell, TableRow } from '@expane/ui'
import { translateData } from 'logic/utils'
import { FC, useEffect } from 'react'
import { Control, Controller, UseFormSetValue, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { SalarySettingFormValues, validateServiceRateSetting } from '../logic'
import { PLACEHOLDERS } from '@expane/logic/form'
import { IoPeopleCircleOutline, IoReaderOutline, IoReceiptOutline } from 'react-icons/io5'
import { useOpenSalarySettingServiceThresholdDialog } from './GroupServiceThresholdsDialog'
import { EditButton } from 'ui/EditButton'
import { useGetCurrencySymbol } from '@expane/logic/currency'
import { observer } from 'mobx-react-lite'
import { store } from 'store'
import { getThresholdsText } from './GroupServiceThresholdsDialog/logic'

interface RateFromServicesItemProps {
  services: ServerExtendedServiceType[]
  control: Control<SalarySettingFormValues>
  setValue: UseFormSetValue<SalarySettingFormValues>
  index: number
  disabled?: boolean
}

export const RateFromServicesItem: FC<RateFromServicesItemProps> = observer(
  ({ services, control, setValue, index, disabled }) => {
    const { t } = useTranslation()

    const currencySymbol = useGetCurrencySymbol(store.branch.branchId)

    const { salarySettingServiceThresholdDialog, openSalarySettingServiceThresholdDialog } =
      useOpenSalarySettingServiceThresholdDialog()

    const watchedDependsOnRealizedValue = useWatch({
      control,
      name: `salarySettingServices.${index}.dependsOnRealizedValue`,
    })

    const watchedType = useWatch({
      control,
      name: `salarySettingServices.${index}.type`,
    })

    const watchedFallbackType = useWatch({
      control,
      name: `salarySettingServices.${index}.fallbackType`,
    })

    const watchedServiceId = useWatch({
      control,
      name: `salarySettingServices.${index}.serviceId`,
    })

    const watchedThresholds = useWatch({
      control,
      name: `salarySettingServices.${index}.thresholds`,
    })

    const service = findById(watchedServiceId, services)
    const isServiceForQuickSale = service?.type === SERVICE_TYPE.forSale
    const isGroupService = service?.type === SERVICE_TYPE.group

    const isFixed = watchedType === RATE_FROM_SERVICES_TYPES.fixedValue.id
    const isGroupTypeWithThresholds = Object.values(RATE_FROM_GROUP_SERVICES_TYPES)
      .map(({ id }) => id)
      .includes(watchedType)

    useEffect(() => {
      if ((isFixed || isGroupTypeWithThresholds) && watchedDependsOnRealizedValue) {
        setValue(`salarySettingServices.${index}.dependsOnRealizedValue`, false)
        setValue(`salarySettingServices.${index}.fallbackType`, undefined)
        setValue(`salarySettingServices.${index}.fallbackValue`, '')
        setValue(`salarySettingServices.${index}.thresholds`, undefined)
      }
    }, [isFixed, watchedDependsOnRealizedValue, index, setValue, isGroupTypeWithThresholds])

    useEffect(() => {
      if (
        (watchedType === RATE_FROM_GROUP_SERVICES_TYPES.percentOfServicePriceByClientsCount.id ||
          watchedType ===
            RATE_FROM_GROUP_SERVICES_TYPES.roundedPercentageOfServicePriceByClientsCount.id) &&
        watchedThresholds
      ) {
        if (watchedThresholds.some(item => Number(item.value) >= 100))
          setValue(`salarySettingServices.${index}.thresholds`, undefined)
      }
    }, [index, isGroupTypeWithThresholds, setValue, watchedThresholds, watchedType])

    useEffect(() => {
      if (isGroupTypeWithThresholds && !watchedThresholds)
        setValue(`salarySettingServices.${index}.thresholds`, [{ qty: '1', value: '' }])
    }, [index, isGroupTypeWithThresholds, setValue, watchedThresholds])

    const rateTypesForSelect = isGroupService
      ? translateData(
          [
            ...Object.values(RATE_FROM_SERVICES_TYPES),
            ...Object.values(RATE_FROM_GROUP_SERVICES_TYPES),
          ],
          t,
        )
      : translateData(Object.values(RATE_FROM_SERVICES_TYPES), t)

    return (
      <TableRow>
        <TableCell>
          <Controller
            name={`salarySettingServices.${index}.name` as 'salarySettingServices.0.name'}
            control={control}
            render={({ field: { value } }) => (
              <div className="flex items-center gap-0.5">
                {isGroupService ? (
                  <IoPeopleCircleOutline size="1rem" className={'shrink-0'} />
                ) : isServiceForQuickSale ? (
                  <IoReceiptOutline size="1rem" className={'shrink-0'} />
                ) : (
                  <IoReaderOutline size="1rem" className={'shrink-0'} />
                )}

                <p className="line-clamp-1 mr-2">{value}</p>
                {isServiceForQuickSale && !isFixed && (
                  <Hint>{t('salarySetting.servicesForSaleHint')}</Hint>
                )}
              </div>
            )}
          />
        </TableCell>

        <TableCell>
          <Controller
            name={`salarySettingServices.${index}.type` as 'salarySettingServices.0.type'}
            control={control}
            render={({ field: { value, onChange } }) => (
              <SelectDropdown
                value={value}
                items={rateTypesForSelect}
                placeholder={t('placeholders.defaultSelect')}
                noDataMessage={t('noSuchRateType')}
                onSelectChange={onChange}
                height="small"
                disabled={disabled}
              />
            )}
          />
        </TableCell>

        {isGroupTypeWithThresholds ? (
          <td colSpan={4}>
            <div className={'pl-2 flex items-center gap-2'}>
              {watchedThresholds && (
                <p>
                  {getThresholdsText(
                    watchedThresholds,
                    getThresholdTypeByGroupRateType(watchedType),
                    currencySymbol,
                  )}
                </p>
              )}
              {!disabled && (
                <EditButton
                  onClick={() =>
                    openSalarySettingServiceThresholdDialog({
                      initialThresholds: watchedThresholds || [],
                      type: getThresholdTypeByGroupRateType(watchedType),
                      onChange: thresholds =>
                        setValue(`salarySettingServices.${index}.thresholds`, thresholds),
                      currencySymbol,
                    })
                  }
                />
              )}
            </div>
          </td>
        ) : (
          <>
            <TableCell>
              <div className="flex">
                <Controller
                  name={`salarySettingServices.${index}.value` as 'salarySettingServices.0.value'}
                  control={control}
                  rules={{
                    validate: value =>
                      validateServiceRateSetting(value, watchedType, watchedDependsOnRealizedValue),
                  }}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <NumberInput
                      containerClassName="w-24 mr-4"
                      placeholder={PLACEHOLDERS.servicePrice}
                      value={value}
                      onChange={onChange}
                      height="small"
                      errorMessage={{ isShown: Boolean(error), text: ' ', reserveIndent: false }}
                      disabled={disabled}
                    />
                  )}
                />
              </div>
            </TableCell>
            <TableCell>
              <Controller
                name={
                  `salarySettingServices.${index}.dependsOnRealizedValue` as 'salarySettingServices.0.dependsOnRealizedValue'
                }
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Checkbox
                    checked={value}
                    onChange={onChange}
                    disabled={disabled || isFixed}
                    label={t('fromRealized')}
                  />
                )}
              />
            </TableCell>
            <TableCell>
              {watchedDependsOnRealizedValue ? (
                <Controller
                  name={
                    `salarySettingServices.${index}.fallbackType` as 'salarySettingServices.0.fallbackType'
                  }
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <SelectDropdown
                      value={value}
                      items={translateData(Object.values(RATE_FROM_SERVICES_FALLBACK_TYPES), t)}
                      placeholder={t('placeholders.defaultSelect')}
                      noDataMessage={t('noSuchRateType')}
                      onSelectChange={onChange}
                      height="small"
                      disabled={
                        disabled ||
                        !watchedDependsOnRealizedValue ||
                        isServiceForQuickSale ||
                        isFixed
                      }
                    />
                  )}
                />
              ) : (
                '-'
              )}
            </TableCell>
            <TableCell>
              {watchedDependsOnRealizedValue ? (
                <Controller
                  name={
                    `salarySettingServices.${index}.fallbackValue` as 'salarySettingServices.0.fallbackValue'
                  }
                  control={control}
                  rules={{
                    validate: value => {
                      if (isServiceForQuickSale) return true

                      return validateServiceRateSetting(
                        value,
                        watchedFallbackType,
                        watchedDependsOnRealizedValue,
                      )
                    },
                  }}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <Input
                      type="number"
                      placeholder={PLACEHOLDERS.servicePrice}
                      value={value}
                      onChange={e => onChange(e.target.value)}
                      height="small"
                      errorMessage={{ isShown: Boolean(error), text: ' ', reserveIndent: false }}
                      disabled={
                        disabled ||
                        !watchedDependsOnRealizedValue ||
                        isServiceForQuickSale ||
                        isFixed
                      }
                    />
                  )}
                />
              ) : (
                '-'
              )}
            </TableCell>
          </>
        )}

        {salarySettingServiceThresholdDialog}
      </TableRow>
    )
  },
)
